// frontend/src/App.js
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { useState, useEffect } from "react";
import Header from "./components/Header";
import { PostsProvider } from "./context/PostsContext";
import BottomNav from "./components/BottomNav";
import SearchBar from "./components/SearchBar";
import SearchModal from "./components/SearchModal";
import Home from "./pages/Home";
import ProductDetail from "./pages/ProductDetail";
import CreatePost from "./components/CreatePost/CreatePost";
import Profile from "./pages/Profile";
import Registro from "./components/Register";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./context/ProtectedRoute";
import Login from "./pages/Login";
import AdminDashboard from "./pages/AdminDashboard";
import Footer from "./components/layout/Footer";
import AuthFooter from "./components/layout/AuthFooter";
import TermosDeUso from "./pages/TermosDeUso";
import PoliticaPrivacidade from "./pages/PoliticaPrivacidade";
import PoliticaComunidade from "./pages/PoliticaComunidade";
import PoliticaPublicidade from "./pages/PoliticaPublicidade";
import PoliticaCookies from "./pages/PoliticaCookies";
import PoliticaReembolso from "./pages/PoliticaReembolso";
import PoliticaValidacao from "./pages/PoliticaValidacao";
import PoliticaChat from "./pages/PoliticaChat";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Contact from "./pages/Contact";

import { CookieProvider } from "./context/CookieContext"; // ✅ CORREGIDO: sin 's'
import CookieBanner from "./components/cookies/CookieBanner";

import ChatList from "./pages/ChatList";
import ChatDetail from "./pages/ChatDetail";
import FAQ from "./pages/FAQ";
import Sobre from "./pages/Sobre";

import ScrollToTop from "./components/ScrollToTop";

// 👇 IMPORTAR EL BANNER DE INSTALACIÓN
import InstallBanner from "./components/InstallBanner";

// Rutas que deben mostrar Footer completo (solo Home)
const FULL_FOOTER_ROUTES = ["/"];

// Rutas que deben mostrar AuthFooter (login y registro)
const AUTH_FOOTER_ROUTES = ["/login", "/registro"];

function AppLayout() {
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedCondominio, setSelectedCondominio] = useState("");
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // 👇 DETECTAR SI ESTAMOS EN EL CHAT
  const isChatPage = location.pathname.startsWith("/chat");

  // 👇 DETECTAR SI ESTAMOS EN ADMIN
  const isAdminPage = location.pathname.startsWith("/admin");

  // 👇 DETECTAR SI ESTAMOS EN PÁGINAS DE AUTENTICACIÓN
  const isAuthPage =
    location.pathname === "/login" || location.pathname === "/registro";

  const showFullFooter = FULL_FOOTER_ROUTES.includes(location.pathname);
  const showAuthFooter = AUTH_FOOTER_ROUTES.includes(location.pathname);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const showSearchBar = location.pathname === "/";

  useEffect(() => {
    setIsSearchModalOpen(false);
  }, [location.pathname]);

  const getMainPadding = () => {
    if (isChatPage) return "pt-0";
    if (showSearchBar) {
      return "pt-[156px] md:pt-[172px]";
    }
    return "pt-[96px] md:pt-[112px]";
  };

  const handleSearch = () => {
    console.log("Búsqueda realizada:", {
      searchTerm,
      selectedCategory,
      selectedCondominio,
    });
  };

  const renderFooter = () => {
    if (isChatPage) return null;
    if (showFullFooter) return <Footer />;
    if (showAuthFooter) return <AuthFooter />;
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {!isChatPage && <Header selectedCondominio={selectedCondominio} />}

      {showSearchBar && !isChatPage && (
        <SearchBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          selectedCondominio={selectedCondominio}
          setSelectedCondominio={setSelectedCondominio}
        />
      )}

      <main className={`${getMainPadding()} flex-grow`}>
        <Routes>
          <Route
            path="/"
            element={
              <Home
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                selectedCondominio={selectedCondominio}
              />
            }
          />
          <Route path="/login" element={<Login />} />
          <Route path="/registro" element={<Registro />} />
          <Route path="/publicar" element={<CreatePost />} />
          <Route
            path="/perfil"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin={true}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/produto/:identifier" element={<ProductDetail />} />

          <Route path="/termos" element={<TermosDeUso />} />
          <Route path="/privacidade" element={<PoliticaPrivacidade />} />
          <Route path="/comunidade" element={<PoliticaComunidade />} />
          <Route path="/publicidade" element={<PoliticaPublicidade />} />
          <Route path="/cookies" element={<PoliticaCookies />} />
          <Route path="/reembolso" element={<PoliticaReembolso />} />
          <Route path="/validacao" element={<PoliticaValidacao />} />
          <Route path="/politica-chat" element={<PoliticaChat />} />
          <Route path="/esqueci-senha" element={<ForgotPassword />} />
          <Route path="/redefinir-senha" element={<ResetPassword />} />
          <Route path="/chat" element={<ChatList />} />
          <Route path="/chat/:id" element={<ChatDetail />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/sobre" element={<Sobre />} />
          <Route path="/contato" element={<Contact />} />
        </Routes>
      </main>

      {/* 👇 BANNER DE INSTALACIÓN */}
      <InstallBanner />

      {!isChatPage && (
        <div className="pb-20 md:pb-0">
          {renderFooter()}
          <BottomNav onSearchClick={() => setIsSearchModalOpen(true)} />
        </div>
      )}

      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        selectedCondominio={selectedCondominio}
        setSelectedCondominio={setSelectedCondominio}
        onSearch={handleSearch}
      />

      <CookieBanner />
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <ScrollToTop />
      <AuthProvider>
        <PostsProvider>
          <CookieProvider>
            <AppLayout />
          </CookieProvider>
        </PostsProvider>
      </AuthProvider>
    </Router>
  );
}
